import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Badge,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { APIClient } from "../../helpers/api_helper";
import { getResidentData } from "../../helpers/residentauth";
import { toast } from "react-toastify";

const api = new APIClient();

/* ── Types ─────────────────────────────────────── */
interface Bill {
  id: number;
  month: number;
  year: number;
  amount: number;
  penalty: number;
  discount: number;
  totalAmount: number;
  dueDate: string;
  status: "PAID" | "UNPAID" | "OVERDUE";
  payment?: {
    id: number;
    paymentDate: string;
    paymentMode: string;
    receipt?: {
      receiptNo: string;
    };
  };
}

interface Notification {
  id: number;
  title: string;
  message: string;
  type: string;
  isRead: boolean;
  billId?: number;
  createdAt: string;
}

interface DashboardStats {
  currentBill: {
    id: number;
    amount: number;
    status: string;
    dueDate: string;
  } | null;
  pendingCount: number;
  pendingAmount: number;
  paidThisYear: number;
  unreadNotifications: number;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const NOTIFICATION_ICONS: Record<string, { icon: string; color: string }> = {
  BILL_GENERATED: { icon: "ri-bill-line", color: "#3B82F6" },
  PENALTY_APPLIED: { icon: "ri-error-warning-line", color: "#DC2626" },
  PAYMENT_SUCCESS: { icon: "ri-check-double-line", color: "#16A34A" },
  DUE_REMINDER: { icon: "ri-time-line", color: "#F59E0B" },
  GENERAL: { icon: "ri-notification-line", color: "#6B7280" },
};

/* ── Main Component ────────────────────────────── */
const ResidentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const residentData = getResidentData();

  const [stats, setStats] = useState<DashboardStats>({
    currentBill: null,
    pendingCount: 0,
    pendingAmount: 0,
    paidThisYear: 0,
    unreadNotifications: 0,
  });
  const [bills, setBills] = useState<Bill[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [billDetailModal, setBillDetailModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  /* ── Fetch All Data ── */
  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Parallel fetch
      const [statsRes, billsRes, notifRes] = await Promise.all([
        api.get("/api/resident/dashboard-stats"),
        api.get("/api/resident/bills"),
        api.get("/api/resident/notifications"),
      ]);

      setStats((statsRes as any)?.data || stats);
      setBills(
        Array.isArray((billsRes as any)?.data) ? (billsRes as any).data : [],
      );
      setNotifications(
        Array.isArray((notifRes as any)?.data) ? (notifRes as any).data : [],
      );
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  /* ── Mark Notification as Read ── */
  const markAsRead = async (id: number) => {
    try {
      await api.put(`/api/resident/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
      );
      setStats((prev) => ({
        ...prev,
        unreadNotifications: Math.max(0, prev.unreadNotifications - 1),
      }));
    } catch {
      toast.error("Failed");
    }
  };

  /* ── Mark All Read ── */
  const markAllRead = async () => {
    try {
      await api.put("/api/resident/notifications/read-all", {});
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setStats((prev) => ({ ...prev, unreadNotifications: 0 }));
      toast.success("All marked as read");
    } catch {
      toast.error("Failed");
    }
  };

  /* ── Calculate Stats ── */
  const pendingBills = useMemo(
    () => bills.filter((b) => b.status !== "PAID"),
    [bills],
  );

  const paidBills = useMemo(
    () => bills.filter((b) => b.status === "PAID").slice(0, 3),
    [bills],
  );

  /* ── Days Until Due ── */
  const getDaysUntilDue = (dueDate: string) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diff = Math.ceil(
      (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    return diff;
  };

  /* ── Time Ago ── */
  const timeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diff = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return past.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
        <p className="text-muted mt-2">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        @keyframes moveStars {
          from { background-position: 0 0; }
          to { background-position: 1000px 500px; }
        }
        .animated-stars-bg {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          background-image: 
            radial-gradient(1.5px 1.5px at 25px 5px, white, transparent),
            radial-gradient(1.5px 1.5px at 50px 25px, white, transparent),
            radial-gradient(1.5px 1.5px at 125px 20px, rgba(255,255,255,0.7), transparent),
            radial-gradient(2px 2px at 50px 75px, white, transparent),
            radial-gradient(2px 2px at 15px 125px, white, transparent),
            radial-gradient(2.5px 2.5px at 110px 80px, rgba(255,255,255,0.8), transparent);
          background-size: 150px 150px;
          animation: moveStars 60s linear infinite;
          opacity: 0.3;
          z-index: 0;
          pointer-events: none;
        }
      `}</style>
      {/* ── Welcome Card ── */}
      <Card
        className="border-0 shadow-sm mb-4"
        style={{
          background: "#1e1b4b",
          color: "#fff",
          borderRadius: "16px",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <div className="animated-stars-bg"></div>
        <CardBody className="p-4" style={{ boxShadow: "rgba(18, 13, 13, 0.24) 0px 3px 8px", position: "relative", zIndex: 1 }}>
          <Row className="align-items-center">
            <Col md={8}>
              <h3 className="mb-2 fw-bold">
                Welcome back, {residentData?.name?.split(" ")[0]}! 👋
              </h3>
              <p className="mb-0" style={{ fontSize: "14px", opacity: 0.95 }}>
                <i className="ri-home-4-line me-1" />
                Unit: <strong>{residentData?.unit.length}</strong><br />
                 {residentData?.unit.map((u:any) => u.unitNo).join(", ")}
              </p>
            </Col>
            <Col md={4} className="text-md-end mt-3 mt-md-0">
              {/* Notification Bell */}
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "48px",
                  height: "48px",
                  color: "#fff",
                  fontSize: "20px",
                  position: "relative",
                  marginRight: "12px",
                }}
              >
                <i className="ri-notification-3-line" />
                {stats.unreadNotifications > 0 && (
                  <Badge
                    color="danger"
                    pill
                    style={{
                      position: "absolute",
                      top: "-2px",
                      right: "-2px",
                      fontSize: "10px",
                      padding: "3px 6px",
                    }}
                  >
                    {stats.unreadNotifications}
                  </Badge>
                )}
              </button>

              {pendingBills.length > 0 && (
                <Button
                  color="light"
                  size="lg"
                  onClick={() => navigate("/resident/bills")}
                  style={{ fontWeight: 600, color: "#4338ca" }}
                >
                  <i className="ri-wallet-line me-2" />
                  Pay ₹{stats.pendingAmount.toLocaleString("en-IN")}
                </Button>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* ── Stats Cards ── */}
      <Row className="g-3 mb-4">
        {[
          {
            label: "Current Month Bill",
            value: stats.currentBill ? `₹${stats.currentBill.amount}` : "—",
            status: stats.currentBill?.status,
            icon: "ri-bill-line",
            color: "#4338ca",
            bg: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
          },
          {
            label: "Total Pending",
            value: `₹${stats.pendingAmount.toLocaleString("en-IN")}`,
            subtext: `${stats.pendingCount} bills`,
            icon: "ri-time-line",
            color: stats.pendingAmount > 0 ? "#dc2626" : "#16a34a",
            bg: "linear-gradient(135deg, #fff8e1, #fef3c7)",
          },
          {
            label: "Paid This Year",
            value: `₹${stats.paidThisYear.toLocaleString("en-IN")}`,
            icon: "ri-check-double-line",
            color: "#16a34a",
            bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
          },
          {
            label: "Notifications",
            value: stats.unreadNotifications,
            subtext: "Unread",
            icon: "ri-notification-3-line",
            color: "#8b5cf6",
            bg: "linear-gradient(135deg, #faf5ff, #f3e8ff)",
          },
        ].map((s, i) => (
          <Col key={i} xs={6} md={3}>
            <Card
              className="stat-card border-0 h-100"
              style={{ borderRadius: "12px", "--stat-color": s.color } as React.CSSProperties}
            >
              <CardBody style={{ background: s.bg, borderRadius: "12px" }}>
                <div className="d-flex align-items-center gap-3">
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      background: "var(--bg-card)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: s.color,
                      fontSize: "24px",
                    }}
                  >
                    <i className={s.icon} />
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: 700,
                        color: s.color,
                        lineHeight: 1.2,
                      }}
                    >
                      {s.value}
                    </div>
                    <div style={{ fontSize: "11px", color: "#475569" }}>
                      {s.label}
                    </div>
                    {s.subtext && (
                      <div
                        style={{
                          fontSize: "10px",
                          color: "#64748b",
                          marginTop: 2,
                        }}
                      >
                        {s.subtext}
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        {/* ── My Bills ── */}
        <Col lg={8}>
          <Card
            className="border-0 shadow-sm mb-4"
            style={{ borderRadius: "12px", background: "var(--bg-card)" }}
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-bold mb-0 text-primary">
                  <i className="ri-bill-line me-2" />
                  My Bills
                </h5>
                <Button
                  color="primary"
                  outline
                  size="sm"
                  onClick={() => navigate("/resident/bills")}
                >
                  View All <i className="ri-arrow-right-line ms-1" />
                </Button>
              </div>

              {/* Pending Bills */}
              {pendingBills.length > 0 ? (
                <div>
                  <h6 className="mb-3" style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                    PENDING ({pendingBills.length})
                  </h6>
                  {pendingBills.slice(0, 3).map((bill) => {
                    const daysLeft = getDaysUntilDue(bill.dueDate);
                    const isOverdue = bill.status === "OVERDUE";

                    return (
                      <div
                        key={bill.id}
                        onClick={() => {
                          setSelectedBill(bill);
                          setBillDetailModal(true);
                        }}
                        style={{
                          padding: "16px",
                          border: `1.5px solid ${isOverdue ? "#fecaca" : "var(--border-color)"}`,
                          borderRadius: "12px",
                          background: isOverdue ? "rgba(220, 38, 38, 0.1)" : "var(--bg-item)",
                          marginBottom: "10px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#4338ca";
                          e.currentTarget.style.boxShadow = "var(--card-shadow)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = isOverdue
                            ? "#fecaca"
                            : "var(--border-color)";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <Row className="align-items-center">
                          <Col xs={7}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <Badge
                                color={isOverdue ? "danger" : "warning"}
                                pill
                                style={{ fontSize: 10 }}
                              >
                                {bill.status}
                              </Badge>
                              <span
                                className="fw-bold"
                                style={{ fontSize: 15 }}
                              >
                                {MONTH_NAMES[bill.month - 1]} {bill.year}
                              </span>
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: 12 }}
                            >
                              <i className="ri-calendar-line me-1" />
                              Due:{" "}
                              {new Date(bill.dueDate).toLocaleString("en-IN", {
                                timeZone: "Asia/Kolkata",
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                              {!isOverdue && daysLeft > 0 && (
                                <span className="ms-2 text-warning fw-medium">
                                  • {daysLeft} days left
                                </span>
                              )}
                              {isOverdue && (
                                <span className="ms-2 text-danger fw-medium">
                                  • Overdue!
                                </span>
                              )}
                            </div>
                            {bill.penalty > 0 && (
                              <div
                                className="text-danger"
                                style={{ fontSize: 11, marginTop: 4 }}
                              >
                                <i className="ri-error-warning-line me-1" />
                                Includes ₹{bill.penalty} penalty
                              </div>
                            )}
                          </Col>
                          <Col xs={5} className="text-end">
                            <div
                              className="fw-bold mb-2"
                              style={{
                                fontSize: 22,
                                color: isOverdue ? "#dc2626" : "#185FA5",
                              }}
                            >
                              ₹{bill.totalAmount.toLocaleString("en-IN")}
                            </div>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate("/resident/bills");
                              }}
                              style={{
                                fontWeight: 600,
                                fontSize: 12,
                                background:
                                  "linear-gradient(135deg, #4f46e5, #4338ca)",
                                border: "none",
                              }}
                            >
                              <i className="ri-wallet-line me-1" />
                              Pay Now
                            </Button>
                          </Col>
                        </Row>
                      </div>
                    );
                  })}

                  {pendingBills.length > 3 && (
                    <Button
                      color="link"
                      block
                      size="sm"
                      onClick={() => navigate("/resident/bills")}
                    >
                      View {pendingBills.length - 3} more pending bills
                    </Button>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div style={{ fontSize: "60px", marginBottom: 8 }}>✅</div>
                  <h6 className="fw-bold text-success">All Paid Up!</h6>
                  <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                    Great job! You have no pending bills.
                  </p>
                </div>
              )}

              {/* Recent Paid Bills */}
              {paidBills.length > 0 && (
                <div
                  className="mt-4 pt-3"
                  style={{ borderTop: "1px solid var(--border-color)" }}
                >
                  <h6 className="mb-3" style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                    RECENT PAYMENTS
                  </h6>
                  {paidBills.map((bill) => (
                    <div
                      key={bill.id}
                      style={{
                        padding: "10px 14px",
                        background: "var(--hover-bg)",
                        borderRadius: "8px",
                        marginBottom: "6px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div className="d-flex align-items-center gap-2">
                          <i className="ri-check-double-line text-success" />
                          <span className="fw-medium" style={{ fontSize: 13 }}>
                            {MONTH_NAMES[bill.month - 1]} {bill.year}
                          </span>
                        </div>
                        {bill.payment && (
                          <small
                            style={{ fontSize: 11, color: "var(--text-secondary)" }}
                          >
                            {new Date(bill.dueDate).toLocaleString("en-IN", {
                              timeZone: "Asia/Kolkata",
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            • {bill.payment.paymentMode}
                          </small>
                        )}
                      </div>
                      <div className="text-end">
                        <div
                          className="fw-bold text-success"
                          style={{ fontSize: 14 }}
                        >
                          ₹{bill.totalAmount}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </Col>

        {/* ── Notifications & Offers ── */}
        <Col lg={4}>
          {/* Notifications */}
          <Card
            className="border-0 shadow-sm mb-4"
            style={{ borderRadius: "12px", background: "var(--bg-card)" }}
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0">
                  <i className="ri-notification-3-line me-2 text-warning" />
                  Notifications
                  {stats.unreadNotifications > 0 && (
                    <Badge
                      color="danger"
                      pill
                      className="ms-2"
                      style={{ fontSize: 10 }}
                    >
                      {stats.unreadNotifications}
                    </Badge>
                  )}
                </h6>
                {stats.unreadNotifications > 0 && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={markAllRead}
                    style={{ fontSize: 11, padding: 0 }}
                  >
                    Mark all read
                  </Button>
                )}
              </div>

              {notifications.length > 0 ? (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {notifications.slice(0, 5).map((notif) => {
                    const iconConfig =
                      NOTIFICATION_ICONS[notif.type] ||
                      NOTIFICATION_ICONS.GENERAL;

                    return (
                      <div
                        key={notif.id}
                        onClick={() => !notif.isRead && markAsRead(notif.id)}
                        style={{
                          padding: "10px",
                          borderRadius: "8px",
                          background: notif.isRead ? "transparent" : "var(--hover-bg)",
                          marginBottom: "6px",
                          cursor: notif.isRead ? "default" : "pointer",
                          borderLeft: `3px solid ${notif.isRead ? "transparent" : iconConfig.color}`,
                        }}
                      >
                        <div className="d-flex gap-2">
                          <div
                            style={{
                              width: "32px",
                              height: "32px",
                              borderRadius: "8px",
                              background: `${iconConfig.color}15`,
                              color: iconConfig.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              fontSize: "16px",
                            }}
                          >
                            <i className={iconConfig.icon} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              className={notif.isRead ? "" : "fw-bold"}
                              style={{ fontSize: 12 }}
                            >
                              {notif.title}
                            </div>
                            <p
                              className="mb-1"
                              style={{
                                fontSize: 11,
                                lineHeight: 1.4,
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                color: "var(--text-secondary)"
                              }}
                            >
                              {notif.message}
                            </p>
                            <small
                              style={{ fontSize: 10, color: "var(--text-secondary)" }}
                            >
                              {timeAgo(notif.createdAt)}
                            </small>
                          </div>
                          {!notif.isRead && (
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: iconConfig.color,
                                marginTop: 6,
                              }}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i
                    className="ri-notification-off-line"
                    style={{ fontSize: 32, opacity: 0.3 }}
                  />
                  <p className="mb-0 mt-2" style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    No notifications yet
                  </p>
                </div>
              )}

              {notifications.length > 5 && (
                <Button
                  color="link"
                  block
                  size="sm"
                  className="mt-2"
                  style={{ fontSize: 12 }}
                >
                  View all notifications
                </Button>
              )}
            </CardBody>
          </Card>

          {/* Discount Offer Card */}
          <Card
            className="border-0 shadow-sm"
            style={{
              borderRadius: "12px",
              background: "linear-gradient(135deg, #fff9e6, #fef3c7)",
              color: "#1e293b"
            }}
          >
            <CardBody>
              <h6 className="fw-bold mb-3">
                <i className="ri-gift-line me-2 text-warning" />
                Save with Advance Pay!
              </h6>
              {[
                { months: 3, discount: 10, amount: 1350 },
                { months: 6, discount: 15, amount: 2550 },
                { months: 12, discount: 20, amount: 4800 },
              ].map((d, i) => (
                <div
                  key={i}
                  style={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    background: "rgba(255,255,255,0.7)",
                    marginBottom: "6px",
                    border: "1px solid #fbbf24",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-bold" style={{ fontSize: 13 }}>
                        {d.months} Months
                      </div>
                      <small className="text-muted" style={{ fontSize: 11 }}>
                        Pay ₹{d.amount}
                      </small>
                    </div>
                    <Badge color="success" pill style={{ fontSize: 11 }}>
                      {d.discount}% OFF
                    </Badge>
                  </div>
                </div>
              ))}
              <Button
                color="warning"
                block
                size="sm"
                className="mt-3 fw-bold"
                onClick={() => navigate("/resident/bills")}
                style={{ borderRadius: "8px", color: "#fff" }}
              >
                <i className="ri-wallet-line me-1" />
                Pay Advance Now
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ── Bill Detail Modal ── */}
      <Modal
        isOpen={billDetailModal}
        toggle={() => setBillDetailModal(false)}
        centered
      >
        <ModalHeader
          toggle={() => setBillDetailModal(false)}
          className="border-0"
        >
          <span className="fw-bold">
            <i className="ri-bill-line me-2 text-primary" />
            Bill Details
          </span>
        </ModalHeader>
        <ModalBody>
          {selectedBill && (
            <div>
              <div
                style={{
                  background: "linear-gradient(135deg, #f0f4ff, #e0e7ff)",
                  padding: "20px",
                  borderRadius: "12px",
                  marginBottom: "16px",
                  textAlign: "center",
                }}
              >
                <div className="text-muted mb-1" style={{ fontSize: 12 }}>
                  {MONTH_NAMES[selectedBill.month - 1]} {selectedBill.year}
                </div>
                <div
                  className="fw-bold"
                  style={{ fontSize: 36, color: "#185FA5" }}
                >
                  ₹{selectedBill.totalAmount.toLocaleString("en-IN")}
                </div>
                <Badge
                  color={
                    selectedBill.status === "PAID"
                      ? "success"
                      : selectedBill.status === "OVERDUE"
                        ? "danger"
                        : "warning"
                  }
                  pill
                  style={{ fontSize: 12, padding: "4px 12px" }}
                >
                  {selectedBill.status}
                </Badge>
              </div>

              <table
                className="table table-borderless"
                style={{ fontSize: 13 }}
              >
                <tbody>
                  <tr>
                    <td className="text-muted">Base Amount:</td>
                    <td className="text-end">₹{selectedBill.amount}</td>
                  </tr>
                  {selectedBill.penalty > 0 && (
                    <tr>
                      <td className="text-muted">Penalty (20%):</td>
                      <td className="text-end text-danger">
                        +₹{selectedBill.penalty}
                      </td>
                    </tr>
                  )}
                  <tr style={{ borderTop: "2px solid #e2e8f0" }}>
                    <td className="fw-bold">Total:</td>
                    <td
                      className="text-end fw-bold"
                      style={{ color: "#185FA5", fontSize: 16 }}
                    >
                      ₹{selectedBill.totalAmount}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Due Date:</td>
                    <td className="text-end">
                      {new Date(selectedBill.dueDate).toLocaleDateString(
                        "en-IN",
                        {
                          timeZone: "Asia/Kolkata",
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </td>
                  </tr>
                  {selectedBill.payment && (
                    <>
                      <tr>
                        <td className="text-muted">Paid On:</td>
                        <td className="text-end">
                          {new Date(
                            selectedBill.payment.paymentDate,
                          ).toLocaleDateString("en-IN", {
                            timeZone: "Asia/Kolkata",
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </td>
                      </tr>
                      <tr>
                        <td className="text-muted">Mode:</td>
                        <td className="text-end">
                          {selectedBill.payment.paymentMode}
                        </td>
                      </tr>
                      {selectedBill.payment.receipt && (
                        <tr>
                          <td className="text-muted">Receipt:</td>
                          <td className="text-end" style={{ fontSize: 11 }}>
                            {selectedBill.payment.receipt.receiptNo}
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="border-0">
          {selectedBill?.status !== "PAID" && (
            <Button
              color="primary"
              block
              onClick={() => {
                setBillDetailModal(false);
                navigate("/resident/bills");
              }}
              style={{
                background: "linear-gradient(135deg, #764ba2, #764ba2)",
                border: "none",
              }}
            >
              <i className="ri-wallet-line me-1" />
              Pay Now
            </Button>
          )}
          <Button color="light" onClick={() => setBillDetailModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ResidentDashboard;
